import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CircleAlert, Plus, Search } from 'lucide-react';
import {
  RNC_SEVERITY_LABEL,
  RNC_STATUS_LABEL,
  UserRole,
  type NonconformityDto,
  type RncSeverity,
  type SupplierListResponseDto,
  type SupplierProductDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useUrlFilter } from '../lib/urlFilter';
import { useAuth } from '../lib/auth';
import { formatCompetence, formatDate } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  Textarea,
} from '../components/ui';

/**
 * `/rncs` — emissão e acompanhamento (PLAN §7, tela 9).
 *
 * **Uma lista para os dois lados.** O servidor já devolve só as lojas de quem
 * pergunta, então o fornecedor vê as RNCs dele e a Weloze vê a carteira. Emitir
 * continua sendo da Weloze; o que muda é quem enxerga a lista, não quem escreve
 * nela.
 *
 * A loja é obrigatória, e não o código do fornecedor: a lista antiga guardava só
 * o código, e por isso era ambígua para quem tem mais de uma loja — que é
 * justamente quem recebe notas diferentes em cada uma.
 *
 * O número é gerado pelo portal, em sequência contínua que nunca reinicia. E não
 * há campo de demérito: ele vem da gravidade, pelo ParameterSet.
 */

type Filter = 'all' | 'open' | 'overdue';

const FILTER_LABEL: Record<Filter, string> = {
  all: 'Todas',
  open: 'Em tratativa',
  overdue: 'Com etapa atrasada',
};

/**
 * Ordenação da lista.
 *
 * Seletor, e não cabeçalho clicável: a RNC é mostrada em cartão, e não em
 * tabela, porque a descrição e as etapas do 8D não cabem numa linha. Sem
 * cabeçalho não há onde clicar — mas a pergunta "quero ver por gravidade" é a
 * mesma, e ela precisa de resposta.
 */
type Ordem = 'recentes' | 'antigas' | 'gravidade' | 'fornecedor';

const ORDEM_LABEL: Record<Ordem, string> = {
  recentes: 'Emissão (mais recentes)',
  antigas: 'Emissão (mais antigas)',
  gravidade: 'Gravidade',
  fornecedor: 'Fornecedor',
};

/** Maior primeiro: é a que custa 50% do IQA e a que trava a nota. */
const SEVERITY_ORDER: Record<RncSeverity, number> = { MAJOR: 0, MEDIUM: 1, MINOR: 2 };

const SEVERITY_TONE: Record<RncSeverity, 'warning' | 'danger' | 'neutral'> = {
  MINOR: 'neutral',
  MEDIUM: 'warning',
  MAJOR: 'danger',
};

export function NonconformitiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  /**
   * `?novo=<storeId>` abre o formulário já com a loja escolhida.
   *
   * É como a ficha do fornecedor emite uma RNC: em vez de duplicar o formulário
   * lá, ela manda para cá com a resposta que já sabe. Quem chega pela ficha não
   * procura o fornecedor numa lista de 44 onde ele já estava na tela.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const lojaInicial = searchParams.get('novo');

  const [filter, setFilter] = useState<Filter>('all');
  // O contador “RNCs com etapa vencida” do painel chega por aqui.
  useUrlFilter('filtro', ['all', 'open', 'overdue'] as const, setFilter);
  const [busca, setBusca] = useState('');
  const [gravidade, setGravidade] = useState<RncSeverity | ''>('');
  const [competencia, setCompetencia] = useState('');
  const [ordem, setOrdem] = useState<Ordem>('recentes');
  const [items, setItems] = useState<NonconformityDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(Boolean(lojaInicial));

  // O parâmetro é um gatilho, não um estado: some da URL assim que o formulário
  // abre, para que um F5 depois de emitir não reabra o modal.
  useEffect(() => {
    if (!lojaInicial) return;
    setCreating(true);
    searchParams.delete('novo');
    setSearchParams(searchParams, { replace: true });
  }, [lojaInicial, searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await api.get<NonconformityDto[]>(`/nonconformities?filter=${filter}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar as RNCs.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  // As competências saem das próprias RNCs: um seletor de todos os meses desde
  // 2026 faria procurar em meses onde nunca houve nada.
  const competencias = useMemo(
    () => [...new Set((items ?? []).map((rnc) => rnc.issuedAt.slice(0, 7)))].sort().reverse(),
    [items],
  );

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtradas = (items ?? []).filter((rnc) => {
      if (gravidade && rnc.severity !== gravidade) return false;
      if (competencia && rnc.issuedAt.slice(0, 7) !== competencia) return false;
      if (!termo) return true;
      return [rnc.supplierName, rnc.protheusRef, rnc.number, rnc.description]
        .some((campo) => (campo ?? '').toLowerCase().includes(termo));
    });

    return filtradas.sort((a, b) => {
      if (ordem === 'gravidade') {
        const diff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
        // Dentro da mesma gravidade, a mais recente primeiro: é a que ainda dá
        // para tratar.
        return diff !== 0 ? diff : b.issuedAt.localeCompare(a.issuedAt);
      }
      if (ordem === 'fornecedor') {
        const diff = a.supplierName.localeCompare(b.supplierName, 'pt-BR');
        return diff !== 0 ? diff : b.issuedAt.localeCompare(a.issuedAt);
      }
      return ordem === 'antigas'
        ? a.issuedAt.localeCompare(b.issuedAt)
        : b.issuedAt.localeCompare(a.issuedAt);
    });
  }, [items, busca, gravidade, competencia, ordem]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">RNCs</h1>
          <p className="mt-1 text-sm text-slate-600">
            Cada RNC pertence a uma loja e entra no IQA da competência em que foi emitida.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden />
            Emitir RNC
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Fornecedor"
          name="busca"
          className="w-64"
          icon={<Search className="size-4" aria-hidden />}
          placeholder="Nome, código, nº da RNC ou descrição"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <Select
          label="Situação"
          name="filtro"
          className="w-52"
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          {(Object.keys(FILTER_LABEL) as Filter[]).map((value) => (
            <option key={value} value={value}>
              {FILTER_LABEL[value]}
            </option>
          ))}
        </Select>
        <Select
          label="Gravidade"
          name="gravidade"
          className="w-40"
          value={gravidade}
          onChange={(e) => setGravidade(e.target.value as RncSeverity | '')}
        >
          <option value="">Todas</option>
          {(Object.keys(SEVERITY_ORDER) as RncSeverity[]).map((value) => (
            <option key={value} value={value}>
              {RNC_SEVERITY_LABEL[value]}
            </option>
          ))}
        </Select>
        <Select
          label="Competência"
          name="competencia"
          className="w-44"
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value)}
        >
          <option value="">Todas</option>
          {competencias.map((valor) => (
            <option key={valor} value={valor}>
              {formatCompetence(valor)}
            </option>
          ))}
        </Select>
        <Select
          label="Ordenar por"
          name="ordem"
          className="w-56"
          value={ordem}
          onChange={(e) => setOrdem(e.target.value as Ordem)}
        >
          {(Object.keys(ORDEM_LABEL) as Ordem[]).map((value) => (
            <option key={value} value={value}>
              {ORDEM_LABEL[value]}
            </option>
          ))}
        </Select>
      </div>

      {error && <Alert>{error}</Alert>}

      {!items ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : visiveis.length === 0 ? (
        <EmptyState
          title="Nenhuma RNC nesta situação"
          description={
            filter === 'all' && !busca && !gravidade && !competencia
              ? 'Nenhuma não conformidade registrada até agora.'
              : 'Troque ou limpe um dos filtros para ver as demais.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {visiveis.map((rnc) => (
            <li
              key={rnc.id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-slate-300"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {/*
                      O título inteiro — número e fornecedor — leva à RNC.
                      Antes só o número levava, e o nome ia para a ficha: o
                      clique mais natural era justamente o que não abria o 8D.
                      A ficha continua alcançável, pelo código ao lado.
                    */}
                    <Link
                      to={`/rncs/${rnc.id}`}
                      className="font-medium text-slate-900 underline-offset-4 hover:underline"
                    >
                      <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-sm">
                        {rnc.number}
                      </span>
                      {rnc.supplierName}
                    </Link>
                    <Link
                      to={`/admin/fornecedores/${rnc.storeId}`}
                      className="font-mono text-xs text-brand-700 underline-offset-4 hover:underline"
                      title="Abrir a ficha do fornecedor"
                    >
                      {rnc.protheusRef}
                    </Link>
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{rnc.description}</p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Badge tone={SEVERITY_TONE[rnc.severity]}>
                    {RNC_SEVERITY_LABEL[rnc.severity]} · −{rnc.demeritPercent}%
                  </Badge>
                  <Badge tone={rnc.status === 'CLOSED' ? 'success' : 'neutral'}>
                    {RNC_STATUS_LABEL[rnc.status]}
                  </Badge>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>emitida em {formatDate(rnc.issuedAt)}</span>
                {rnc.stages.map((stage) => (
                  <span
                    key={stage.key}
                    className={stage.overdue ? 'flex items-center gap-1 text-red-600' : ''}
                  >
                    {stage.overdue && <CircleAlert className="size-3" aria-hidden />}
                    {stage.label}: {stage.doneAt ? formatDate(stage.doneAt) : stage.dueAt ? `prazo ${formatDate(stage.dueAt)}` : '—'}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      <CreateRncModal
        open={creating}
        storeId={lojaInicial}
        onClose={() => setCreating(false)}
        onDone={() => {
          setCreating(false);
          void load();
        }}
      />
    </div>
  );
}

function CreateRncModal({
  open,
  storeId,
  onClose,
  onDone,
}: {
  open: boolean;
  /** Pré-seleção vinda da ficha do fornecedor (`?novo=`). */
  storeId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [stores, setStores] = useState<SupplierListResponseDto['items']>([]);
  const [nextNumber, setNextNumber] = useState<string | null>(null);
  const [form, setForm] = useState({
    storeId: storeId ?? '',
    issuedAt: new Date().toISOString().slice(0, 10),
    severity: 'MAJOR' as RncSeverity,
    description: '',
    productCode: '',
    productName: '',
    invoiceRef: '',
    productionOrder: '',
    lotQuantity: '',
    quantityAffected: '',
  });
  const [produtos, setProdutos] = useState<SupplierProductDto[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFields({});
    setForm((atual) => (storeId ? { ...atual, storeId } : atual));
    void (async () => {
      try {
        const [lista, proximo] = await Promise.all([
          api.get<SupplierListResponseDto>('/suppliers?scope=monitored'),
          api.get<{ number: string }>(`/nonconformities/next-number?issuedAt=${form.issuedAt}`),
        ]);
        setStores(lista.items);
        setNextNumber(proximo.number);
      } catch {
        setNextNumber(null);
      }
    })();
    // A dependência é só a abertura: recarregar a cada tecla da data seria uma
    // requisição por dígito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storeId]);

  /**
   * Os produtos daquela loja, para o código puxar o nome.
   *
   * Sai do histórico de compras já importado, e não do cadastro do ERP: quem
   * emite RNC está falando de algo que chegou. Quem nunca comprou não aparece —
   * por isso os dois campos continuam digitáveis, e a lista só adianta o
   * trabalho.
   */
  useEffect(() => {
    if (!open || !form.storeId) {
      setProdutos([]);
      return;
    }
    void (async () => {
      try {
        setProdutos(await api.get<SupplierProductDto[]>(`/stores/${form.storeId}/products`));
      } catch {
        setProdutos([]);
      }
    })();
  }, [open, form.storeId]);

  /** Digitou (ou escolheu) o código: se ele está no histórico, o nome vem junto. */
  function mudaCodigo(valor: string) {
    const conhecido = produtos.find((produto) => produto.code === valor.trim());
    setForm((atual) => ({
      ...atual,
      productCode: valor,
      ...(conhecido ? { productName: conhecido.name ?? '' } : {}),
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFields({});
    try {
      await api.post('/nonconformities', {
        storeId: form.storeId,
        issuedAt: form.issuedAt,
        severity: form.severity,
        description: form.description,
        productCode: form.productCode || null,
        productName: form.productName || null,
        invoiceRef: form.invoiceRef || null,
        productionOrder: form.productionOrder || null,
        lotQuantity: form.lotQuantity === '' ? null : Number(form.lotQuantity),
        quantityAffected: form.quantityAffected === '' ? null : Number(form.quantityAffected),
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível emitir a RNC.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Emitir RNC"
      description={
        nextNumber
          ? `Receberá o número ${nextNumber} — a sequência é contínua e não reinicia por ano.`
          : 'O portal gera o número automaticamente.'
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Select
          label="Loja do fornecedor"
          name="storeId"
          value={form.storeId}
          onChange={(e) => setForm({ ...form, storeId: e.target.value })}
          error={fields.storeId}
          hint="A RNC pertence à loja, não ao código — é ela que recebe a nota."
        >
          <option value="">Selecione…</option>
          {stores.map((store) => (
            <option key={store.storeId} value={store.storeId}>
              {store.companyName} · {store.protheusRef}
            </option>
          ))}
        </Select>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Data de emissão"
            name="issuedAt"
            type="date"
            value={form.issuedAt}
            onChange={(e) => setForm({ ...form, issuedAt: e.target.value })}
            error={fields.issuedAt}
            hint="Define a competência do IQA."
          />
          <Select
            label="Gravidade"
            name="severity"
            value={form.severity}
            onChange={(e) => setForm({ ...form, severity: e.target.value as RncSeverity })}
            hint="O demérito vem daqui — não há campo de percentual."
          >
            {(Object.keys(RNC_SEVERITY_LABEL) as RncSeverity[]).map((value) => (
              <option key={value} value={value}>
                {RNC_SEVERITY_LABEL[value]}
              </option>
            ))}
          </Select>
        </div>

        <Textarea
          label="Descrição do problema"
          name="description"
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          error={fields.description}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Código do item"
            name="productCode"
            list="rnc-produtos"
            hint={
              produtos.length > 0
                ? `${produtos.length} itens já comprados desta loja — o nome vem junto.`
                : 'Sem histórico de compras importado para esta loja.'
            }
            value={form.productCode}
            onChange={(e) => mudaCodigo(e.target.value)}
          />
          <datalist id="rnc-produtos">
            {produtos.map((produto) => (
              <option key={produto.code} value={produto.code}>
                {produto.name ?? ''}
              </option>
            ))}
          </datalist>
          <Field
            label="Descrição do item"
            name="productName"
            hint="Preenchida pelo código, e editável."
            value={form.productName}
            onChange={(e) => setForm({ ...form, productName: e.target.value })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Nota fiscal"
            name="invoiceRef"
            value={form.invoiceRef}
            onChange={(e) => setForm({ ...form, invoiceRef: e.target.value })}
          />
          <Field
            label="Ordem de produção"
            name="productionOrder"
            hint="Opcional."
            value={form.productionOrder}
            onChange={(e) => setForm({ ...form, productionOrder: e.target.value })}
          />
          {/*
            As duas quantidades juntas, porque é a razão entre elas que informa:
            "12 peças não conformes" não diz se o problema foi pontual ou se o
            lote inteiro está comprometido.
          */}
          <Field
            label="Quantidade do lote"
            name="lotQuantity"
            type="number"
            min={0}
            inputMode="decimal"
            value={form.lotQuantity}
            onChange={(e) => setForm({ ...form, lotQuantity: e.target.value })}
          />
          <Field
            label="Quantidade não conforme"
            name="quantityAffected"
            type="number"
            min={0}
            inputMode="decimal"
            error={fields.quantityAffected}
            value={form.quantityAffected}
            onChange={(e) => setForm({ ...form, quantityAffected: e.target.value })}
          />
        </div>

        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Os prazos do 8D são calculados a partir da emissão: contenção em 24 h, retorno em 15
          dias, evidências em 60 e avaliação da eficácia em 90 (RQ 230-01 §6.1).
        </p>

        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving} disabled={!form.storeId || !form.description.trim()}>
            Emitir
          </Button>
        </div>
      </form>
    </Modal>
  );
}
